import styled from "@emotion/styled";
import { useMemo } from "react";
import { NavLink as RouterNavLink, useMatches } from "react-router";

import { SkiliticsIcon } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { useFeatureSwitch } from "@skilitics/nautilus-core-designer";

import { sidebarItemCss } from "./sidebarStyles";

const Container = styled.nav<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;

  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
`;

const NavLink = styled(RouterNavLink)`
  height: 36px;
  ${sidebarItemCss}
`;

const Separator = styled.div`
  height: 1px;
  margin: 6px 0;
  background: ${({ theme }) => theme.outlineSoft};
`;

export const SidebarNav = () => {
  const formatMessage = useFormatMessage(formatStrings);

  // surely there's a reasonable way to type route handles...
  const matches = useMatches();
  const disabled = useMemo(
    () =>
      matches.some(
        (m) =>
          typeof m.handle === "object" &&
          m.handle !== null &&
          "disableHeaderNavigation" in m.handle &&
          m.handle?.disableHeaderNavigation,
      ),
    [matches],
  );

  const moduleSetsAvailable = useFeatureSwitch("module-sets");
  const hasWorkspaces = useFeatureSwitch("org-admin");

  return (
    <Container $disabled={disabled} data-test="navigation-container">
      <NavLink to="/modules" data-test="projects">
        <SkiliticsIcon icon="layer-grid" />
        {formatMessage("projects")}
      </NavLink>

      <NavLink to="/delivery" data-test="delivery">
        <SkiliticsIcon icon="delivery" />
        {formatMessage("delivery")}
      </NavLink>

      <Separator />

      {hasWorkspaces && (
        <NavLink to="/settings/workspace/members" data-test="invite-team">
          <SkiliticsIcon icon="send-mail" />
          {formatMessage("inviteTeam")}
        </NavLink>
      )}

      <NavLink to="/settings/workspace" end data-test="administration">
        <SkiliticsIcon icon="advanced-settings" size={16} />
        {formatMessage("administration")}
      </NavLink>

      {moduleSetsAvailable && (
        <NavLink to="/module-sets" data-test="builders">
          <SkiliticsIcon icon="delivery" />
          {formatMessage("builders")}
        </NavLink>
      )}

      <Separator />
    </Container>
  );
};

const formatStrings = {
  projects: {
    en: "Projects",
  },
  builders: {
    en: "Module Sets",
  },
  delivery: {
    en: "Delivery",
  },
  inviteTeam: {
    en: "Invite your Team",
  },
  administration: {
    en: "Administration",
  },
} satisfies FormatMessages;
